import React, { useState, useEffect } from 'react';
import '../../../styles/patent-cost-estimate.css';
import { Disc } from 'lucide-react';
import Disclaimer from '../../../components/disclaimer';

const PatentCostEstimate = () => {
  const [patentType, setPatentType] = useState('software');
  const [entitySize, setEntitySize] = useState('micro');
  const [complexity, setComplexity] = useState(4);
  const [claimsCount, setClaimsCount] = useState(20);
  const [officeActions, setOfficeActions] = useState(4);
  const [services, setServices] = useState({
    priorArtSearch: true,
    provisionalFirst: false,
    fastTrack: false,
    pctFiling: false,
    appealProcess: false,
    maintenanceFees: false
  });
  const [currentCosts, setCurrentCosts] = useState({});

  // Comprehensive cost data based on multiple industry sources
  const costData = {
    // IPWatchdog complexity-based attorney fees
    complexityLevels: {
      1: { name: "Extremely Simple", min: 5000, max: 7000 },    // electric switch, coat hanger
      2: { name: "Relatively Simple", min: 7000, max: 8500 },   // board game, umbrella
      3: { name: "Minimally Complex", min: 8500, max: 10000 },  // power tool, lawn mower
      4: { name: "Moderately Complex", min: 10000, max: 12000 }, // RFID, cell phone
      5: { name: "Relatively Complex", min: 12000, max: 14000 }, // prosthetic device
      6: { name: "Highly Complex", min: 14000, max: 16000 },     // MRI, PCR, telecom
      7: { name: "Software/Business Method", min: 16000, max: 25000 } // Software, automated systems
    },
    
    // Patent type multipliers and base costs
    patentTypes: {
      software: { base: 16000, multiplier: 1.5, name: "Software" },     // Highest due to Alice requirements
      electrical: { base: 10000, multiplier: 1.2, name: "Electrical" }, 
      mechanical: { base: 8500, multiplier: 1.0, name: "Mechanical" },
      medical: { base: 12000, multiplier: 1.4, name: "Medical/Biotech" },
      design: { base: 2000, multiplier: 0.3, name: "Design" },          // Much simpler
      simple: { base: 7000, multiplier: 0.8, name: "Simple Utility" }
    },
    
    // 2025 USPTO fees by entity size
    uspteFees: {
      micro: { 
        filing: 64, search: 80, examination: 120, issue: 300,
        rce: 800, appeal: 400, fastTrack: 1600
      },
      small: { 
        filing: 128, search: 160, examination: 240, issue: 600,
        rce: 1200, appeal: 800, fastTrack: 3200
      },
      large: { 
        filing: 320, search: 400, examination: 480, issue: 1200,
        rce: 1600, appeal: 1600, fastTrack: 4000
      }
    },
    
    // Service costs from industry sources
    services: {
      priorArtSearch: { 
        simple: { min: 1000, max: 1250 },
        moderate: { min: 1500, max: 2000 },
        complex: { min: 2000, max: 3000 }
      },
      provisionalApp: {
        mechanical: { min: 2000, max: 3000 },
        software: { min: 6000, max: 10000 },
        general: { min: 2500, max: 4000 }
      },
      officeActionResponse: { min: 1500, max: 4000 }, // BlueIron: $4000 average
      pctFiling: { min: 2500, max: 3500 },
      fullAppeal: 10500, // IPWatchdog data
      preAppealConference: 4000, // BlueIron data
      professionalDrawings: { perPage: 125, typical: 400 }
    },
    
    // Maintenance fees over patent life (utility patents only)
    maintenanceFees: {
      micro: [400, 752, 1540],    // 3.5, 7.5, 11.5 years
      small: [800, 1504, 3080],
      large: [2000, 3760, 7700]
    }
  };

  const calculateCosts = () => {
    const newCosts = {};

    // Base attorney fees based on complexity and patent type
    const complexityData = costData.complexityLevels[complexity];
    const typeData = costData.patentTypes[patentType];
    
    let baseAttorneyFee;
    if (patentType === 'software') {
      // Software always uses complexity level 7 pricing due to Alice requirements
      baseAttorneyFee = costData.complexityLevels[7].min + 
        (costData.complexityLevels[7].max - costData.complexityLevels[7].min) * 0.5;
    } else if (patentType === 'design') {
      // Design patents are much simpler
      baseAttorneyFee = typeData.base;
    } else {
      // Regular utility patents use complexity-based pricing
      baseAttorneyFee = complexityData.min + 
        (complexityData.max - complexityData.min) * 0.5;
      baseAttorneyFee *= typeData.multiplier;
    }
    
    // Claims adjustment (excess claims cost more)
    const excessClaims = Math.max(0, claimsCount - 20);
    const claimsAdjustment = excessClaims * 150; // $150 per excess claim

    newCosts['Patent Attorney Fees'] = Math.round(baseAttorneyFee + claimsAdjustment);

    // USPTO fees
    const fees = costData.uspteFees[entitySize];
    if (patentType !== 'design') {
      newCosts['USPTO Filing Fee'] = fees.filing;
      newCosts['USPTO Search Fee'] = fees.search;
      newCosts['USPTO Examination Fee'] = fees.examination;
      newCosts['USPTO Issue Fee'] = fees.issue;
    } else {
      // Design patents have different fee structure
      newCosts['USPTO Design Patent Fees'] = 88 + 160 + 300; // Filing + examination + issue for small entity
    }

    // Professional drawings (required)
    const typicalDrawings = patentType === 'software' ? 3 : 4;
    newCosts['Professional Drawings'] = costData.services.professionalDrawings.perPage * typicalDrawings;

    // Prior art search
    if (services.priorArtSearch) {
      let searchCost;
      if (complexity <= 2) {
        searchCost = costData.services.priorArtSearch.simple;
      } else if (complexity <= 5) {
        searchCost = costData.services.priorArtSearch.moderate;
      } else {
        searchCost = costData.services.priorArtSearch.complex;
      }
      newCosts['Prior Art Search & Opinion'] = Math.round((searchCost.min + searchCost.max) / 2);
    }

    // Provisional application
    if (services.provisionalFirst) {
      let provCost;
      if (patentType === 'software') {
        provCost = costData.services.provisionalApp.software;
      } else if (patentType === 'mechanical') {
        provCost = costData.services.provisionalApp.mechanical;
      } else {
        provCost = costData.services.provisionalApp.general;
      }
      newCosts['Provisional Patent Application'] = Math.round((provCost.min + provCost.max) / 2);
      newCosts['Provisional USPTO Filing Fee'] = entitySize === 'micro' ? 64 : entitySize === 'small' ? 130 : 320;
    }

    // Office action responses - industry average is 4.2 but users can adjust
    const avgResponseCost = (costData.services.officeActionResponse.min + costData.services.officeActionResponse.max) / 2;
    newCosts['Office Action Responses'] = Math.round(avgResponseCost * officeActions);

    // RCE fees (typically needed after every 2 office actions)
    const rceCount = Math.floor(officeActions / 2);
    if (rceCount > 0) {
      newCosts['RCE Fees (USPTO)'] = fees.rce * rceCount;
    }

    // Fast track processing
    if (services.fastTrack) {
      newCosts['Fast Track (Track One) Fee'] = fees.fastTrack;
    }

    // PCT filing
    if (services.pctFiling) {
      const pctCost = costData.services.pctFiling;
      newCosts['PCT International Filing'] = Math.round((pctCost.min + pctCost.max) / 2);
    }

    // Appeal process (25% likelihood according to BlueIron data)
    if (services.appealProcess) {
      newCosts['Pre-Appeal Conference'] = costData.services.preAppealConference;
      newCosts['Appeal Fee (USPTO)'] = fees.appeal;
      newCosts['Full Appeal (25% likelihood)'] = Math.round(costData.services.fullAppeal * 0.25);
    }

    // Maintenance fees (only for utility patents)
    if (services.maintenanceFees && patentType !== 'design') {
      const maintenanceFees = costData.maintenanceFees[entitySize];
      const totalMaintenance = maintenanceFees.reduce((sum, fee) => sum + fee, 0);
      newCosts['Maintenance Fees (20-year life)'] = totalMaintenance;
    }

    setCurrentCosts(newCosts);
  };

  const handleServiceChange = (serviceName) => {
    setServices(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const getComplexityLabel = () => {
    return costData.complexityLevels[complexity].name;
  };

  const getOfficeActionsLabel = () => {
    return officeActions === 4 ? '4 (Industry Average)' : officeActions.toString();
  };

  const getTotalCost = () => {
    return Object.values(currentCosts).reduce((sum, cost) => sum + cost, 0);
  };

  const getSortedCostItems = () => {
    const sortOrder = [
      'Patent Attorney Fees', 'Professional Drawings', 'Prior Art Search & Opinion', 
      'Provisional Patent Application', 'Provisional USPTO Filing Fee',
      'USPTO Filing Fee', 'USPTO Search Fee', 'USPTO Examination Fee', 'USPTO Issue Fee', 'USPTO Design Patent Fees'
    ];

    const sortedKeys = Object.keys(currentCosts).sort((a, b) => {
      const aIndex = sortOrder.indexOf(a);
      const bIndex = sortOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return sortedKeys.map(key => ({
      label: key,
      value: currentCosts[key]
    }));
  };

  useEffect(() => {
    calculateCosts();
  }, [patentType, entitySize, complexity, claimsCount, officeActions, services]);

  return (
    <div className="patent-cost-container patent-cost-animate-in">
      <div className="patent-cost-header">
        <h1>Patent Cost Estimate Calculator</h1>
        <p className='text-white'>Comprehensive cost estimates based on industry data from leading patent firms and IP organizations</p>
      </div>
      
      <div className="patent-cost-main-content">
        <div className="patent-cost-form-section">
          <div className="patent-cost-form-group">
            <label htmlFor="patentType">Patent Type</label>
            <select 
              id="patentType" 
              value={patentType} 
              onChange={(e) => setPatentType(e.target.value)}
            >
              <option value="software">Software/Business Method Patent</option>
              <option value="electrical">Electrical/Electronics Patent</option>
              <option value="mechanical">Mechanical Patent</option>
              <option value="medical">Medical Device/Biotech Patent</option>
              <option value="design">Design Patent</option>
              <option value="simple">Simple Utility Patent</option>
            </select>
          </div>

          <div className="patent-cost-form-group">
            <label htmlFor="entitySize">Entity Size</label>
            <select 
              id="entitySize" 
              value={entitySize} 
              onChange={(e) => setEntitySize(e.target.value)}
            >
              <option value="micro">Micro Entity (Individual, &lt;$200k revenue)</option>
              <option value="small">Small Entity (&lt;500 employees)</option>
              <option value="large">Large Entity (500+ employees)</option>
            </select>
          </div>

          <div className="patent-cost-form-group">
            <label htmlFor="complexity">
              Invention Complexity 
              <span className="patent-cost-range-value">{getComplexityLabel()}</span>
            </label>
            <div className="patent-cost-range-container">
              <input 
                type="range" 
                id="complexity" 
                min="1" 
                max="7" 
                value={complexity}
                onChange={(e) => setComplexity(parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="patent-cost-form-group">
            <label htmlFor="claimsCount">
              Number of Claims 
              <span className="patent-cost-range-value">{claimsCount}</span>
            </label>
            <div className="patent-cost-range-container">
              <input 
                type="range" 
                id="claimsCount" 
                min="5" 
                max="100" 
                value={claimsCount}
                onChange={(e) => setClaimsCount(parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="patent-cost-form-group">
            <label htmlFor="officeActions">
              Expected Office Actions 
              <span className="patent-cost-range-value">{getOfficeActionsLabel()}</span>
            </label>
            <div className="patent-cost-range-container">
              <input 
                type="range" 
                id="officeActions" 
                min="1" 
                max="10" 
                value={officeActions}
                onChange={(e) => setOfficeActions(parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="patent-cost-form-group">
            <label>Additional Services</label>
            <div className="patent-cost-checkbox-group">
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="priorArtSearch" 
                  checked={services.priorArtSearch}
                  onChange={() => handleServiceChange('priorArtSearch')}
                />
                <label htmlFor="priorArtSearch">Professional Prior Art Search & Opinion</label>
              </div>
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="provisionalFirst"
                  checked={services.provisionalFirst}
                  onChange={() => handleServiceChange('provisionalFirst')}
                />
                <label htmlFor="provisionalFirst">File Provisional Application First</label>
              </div>
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="fastTrack"
                  checked={services.fastTrack}
                  onChange={() => handleServiceChange('fastTrack')}
                />
                <label htmlFor="fastTrack">Fast Track Processing (Track One)</label>
              </div>
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="pctFiling"
                  checked={services.pctFiling}
                  onChange={() => handleServiceChange('pctFiling')}
                />
                <label htmlFor="pctFiling">International PCT Filing</label>
              </div>
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="appealProcess"
                  checked={services.appealProcess}
                  onChange={() => handleServiceChange('appealProcess')}
                />
                <label htmlFor="appealProcess">Include Appeal Process (25% likelihood)</label>
              </div>
              <div className="patent-cost-checkbox-item">
                <input 
                  type="checkbox" 
                  id="maintenanceFees"
                  checked={services.maintenanceFees}
                  onChange={() => handleServiceChange('maintenanceFees')}
                />
                <label htmlFor="maintenanceFees">Include Maintenance Fees (20-year life)</label>
              </div>
            </div>
          </div>
          <hr className='my-5'/>
          <Disclaimer />
        </div>

        <div className="patent-cost-results-section">
          <div className="patent-cost-results-card">
            <h2 className="patent-cost-results-title">Cost Breakdown</h2>
            <div>
              {getSortedCostItems().map((item) => (
                <div key={item.label} className="patent-cost-cost-item">
                  <span className="patent-cost-cost-label">{item.label}</span>
                  <span className="patent-cost-cost-value">${item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="patent-cost-total-cost">
              <div className="amount">${getTotalCost().toLocaleString()}</div>
              <div className="label">Estimated Total Cost</div>
            </div>
          </div>

          <div className="patent-cost-info-section">
            <h3>💡 Cost Factors & Industry Data</h3>
            <ul>
              <li><strong>US Average Patent Cost:</strong> $50,000 - $60,000 (BlueIron IP study)</li>
              <li><strong>Preparation & Filing:</strong> $5,000 - $16,000+ depending on complexity</li>
              <li><strong>Average Office Actions:</strong> 4.2 per patent (industry standard)</li>
              <li><strong>Prosecution Costs:</strong> $20,000 on average through to grant</li>
              <li><strong>Software Patents:</strong> Require extensive technical detail, highest cost category</li>
            </ul>
            <div className="patent-cost-citation">
              Sources: BlueIron IP Average Patent Cost Study, IPWatchdog Patent Cost Analysis (2015), 
              Rapacke Law Group Patent Pricing Guide, American Intellectual Property Lawyers Association Survey
            </div>
          </div>

          <div className="patent-cost-disclaimer">
            <h3>⚠️ Important Note</h3>
            <p>This estimate is based on industry data from multiple patent law firms and organizations. Actual costs may vary significantly based on your specific invention, chosen attorney, and prosecution complexity.</p>
            <p><strong>Consult with an attorney at <a href="https://www.patentpc.com" target="_blank" rel="noopener noreferrer">www.patentpc.com</a> and ask for 50% discount from industry standard.</strong></p>
            <p>Patent costs can range from $12,000 for simple inventions to $80,000+ for complex technologies requiring extensive prosecution.</p>
          </div>

          <div className="patent-cost-cta-section">
            <a 
              href="https://app.fyxer.com/e/bao-tran-b31/15" 
              target="_blank" 
              rel="noopener noreferrer"
              className="patent-cost-cta-button"
            >
              <span className="patent-cost-cta-icon">📞</span>
              <span className="patent-cost-cta-text">Schedule Free PatentPC Call</span>
              <span className="patent-cost-cta-subtitle">Get personalized cost estimates</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatentCostEstimate;